import { useCallback, useMemo, useRef, useState } from "react";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import { ClientEmployeeFileUploadsTable } from "@components/Tables/ClientEmployeeFileUploadsTable/ClientEmployeeFileUploadsTable";
import {
  ClientEmployeeFileUploadsTableConfig,
} from "@components/Tables/ClientEmployeeFileUploadsTable/ClientEmployeeFileUploadsTableConfig";
import { ClientEmployeeFileUploadsTableColumnCustomization } from "@components/Tables/ClientEmployeeFileUploadsTable/UI/ClientEmployeeFileUploadsTableColumnCustomization";
import {
  DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_ORDER,
  DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_SETTINGS,
} from "@components/Tables/ClientEmployeeFileUploadsTable/UI/ClientEmployeeFileUploadsTableColumns";
import {
  ClientEmployeeFileUploadsFilterFormRef,
  ClientEmployeeFileUploadsFilterFormValues,
  ClientEmployeeFileUploadsTableFilters,
} from "@components/Tables/ClientEmployeeFileUploadsTable/UI/ClientEmployeeFileUploadsTableFilters";

import { Box } from "@design-system/Box/Box";
import { Card } from "@design-system/Card/Card";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

type Props = {
  clientId?: string;
  employeeId?: string;
  applicationId?: string;
  initialPageIndex?: number;
  showFooter?: boolean;
  showHeader?: boolean;
  showPageSizeDropdown?: boolean;
  pageSize?: number;
};

export const ClientEmployeeFileUploadsCard = ({
  clientId,
  employeeId,
  applicationId,
  initialPageIndex = 0,
  showFooter = true,
  showHeader = true,
  showPageSizeDropdown = true,
  pageSize = 30,
}: Props) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<ClientEmployeeFileUploadsFilterFormValues>({
      fileNameSearch: "",
      mimetypeSearch: "",
      datetime_Gte: undefined,
      datetime_Lte: undefined,
      employeeId: undefined,
      applicationId: undefined,
    });

  const filterFormRef = useRef<ClientEmployeeFileUploadsFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return (
      !!filterValues.fileNameSearch ||
      !!filterValues.mimetypeSearch ||
      !!filterValues.datetime_Gte ||
      !!filterValues.datetime_Lte ||
      !!filterValues.employeeId ||
      !!filterValues.applicationId
    );
  }, [filterValues]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues({
      fileNameSearch: "",
      mimetypeSearch: "",
      datetime_Gte: undefined,
      datetime_Lte: undefined,
      employeeId: undefined,
      applicationId: undefined,
    });
  }, []);

  const handleFilterChange = useCallback(
    (newValues: ClientEmployeeFileUploadsFilterFormValues) => {
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setIsFilterPanelOpen(false);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  return (
    <TableColumnSettingsProvider
      tableId={ClientEmployeeFileUploadsTableConfig.tableId}
      defaultColumnSettings={DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_SETTINGS}
      defaultColumnOrder={DEFAULT_CLIENT_EMPLOYEE_FILE_UPLOAD_TABLE_COLUMN_ORDER}
    >
      <Card grow>
        <Card.Header withBottomPadding>
          <Card.Header.Title>File Uploads</Card.Header.Title>
          <Card.Header.Actions>
            <Box direction="row" gap={8} align="center">
              {filtersActive && (
                <Text size={12} color={"textSecondary"}>
                  Filters are applied
                </Text>
              )}
              <IconButton
                size="sm"
                icon="faFilter"
                onClick={handleFilterClick}
                outline={"sm"}
                showBubble={filtersActive}
                tooltip="Show filters"
              />
              <IconButton
                size="sm"
                icon="faTableColumns"
                onClick={handleColumnSettingsClick}
                outline={"sm"}
                tooltip="Show/Hide columns"
              />
            </Box>
          </Card.Header.Actions>
        </Card.Header>
        <Card.Body noPadding grow>
          <ClientEmployeeFileUploadsTable
            clientId={clientId}
            employeeId={employeeId}
            applicationId={applicationId}
            initialPageIndex={initialPageIndex}
            showFooter={showFooter}
            showHeader={showHeader}
            showPageSizeDropdown={showPageSizeDropdown}
            pageSize={pageSize}
            filterValues={filterValues}
          />
        </Card.Body>
      </Card>

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title="Filters"
      >
        <ClientEmployeeFileUploadsTableFilters
          ref={filterFormRef}
          values={filterValues}
          onFilterChange={handleFilterChange}
          clientId={clientId}
          hideEmployeeFilter={!!employeeId}
          hideApplicationFilter={!!applicationId}
        />
      </SlideInPanel>

      <ClientEmployeeFileUploadsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
      />
    </TableColumnSettingsProvider>
  );
};
